import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { CURRENCY_TYPE, FUNNEL_TYPE } from '../../../enum/constants';
import { ManagerOption, WarehouseService } from '../../../services/warehouse.service';
import { BaseService } from '../../../services/base.service';
import { StackedSidebarService } from '../../../services/stacked-sidebar.service';
import { SidebarType } from '../../layout/stacked-sidebar/stacked-sidebar.interface';
import { formatFullName } from '../../../helpers';
import { formatMoneyAmount } from '../../board-v3/price-field.utils';
import { TableV2Action, TableV2Column } from '../../table-v2/table-v2.interface';
import { VariantPick } from '../variant-picker/variant-picker.component';
import {
  StockDocument,
  StockDocumentLine,
  StockDocumentType,
  StockShortage,
  TransferCloseResolution,
  VariantSummary,
  Warehouse,
} from '../warehouse.types';
import {
  CURRENCY_OPTIONS,
  DOCUMENT_STATUS_CLASSES,
  DOCUMENT_TYPE_STYLES,
  formatQuantity,
  formatSignedQuantity,
  getApiError,
  getDocumentNumber,
  getStockShortages,
  getVariantCodes,
  getVariantTitle,
  isOpenTransfer,
  parseNumber,
} from '../warehouse.utils';

/** A document line as it is edited: the typed numbers stay text, the fields are named as the API names them */
interface LineRow {
  variant_id: string;
  /** Null when the variant was deleted */
  variant: VariantSummary | null;
  quantity: string;
  price: string;
  actual_quantity: string;
  /** Transfer: the quantity to accept now */
  accept: string;
  /** The saved line, for the posted view */
  line: StockDocumentLine | null;
  invalid: Set<LineField>;
}

type LineField = 'quantity' | 'price' | 'actual_quantity' | 'accept';

/**
 * A stock document in one sidebar: a draft is edited, saved, posted or deleted; a posted one is cancelled;
 * an open transfer is accepted line by line and closed with a return or a loss.
 */
@Component({
  selector: 'app-warehouse-document-detail',
  templateUrl: './document-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class WarehouseDocumentDetailComponent implements OnInit {
  @Input() documentId: string | null = null;
  /** Type of a new document, then of the loaded one */
  @Input() type: StockDocumentType = 'RECEIPT';
  /** Whether the document changed: the lists have to reload */
  @Output() close = new EventEmitter<boolean>();

  private readonly warehouseService = inject(WarehouseService);
  private readonly baseService = inject(BaseService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form = new FormGroup({
    warehouse_id: new FormControl<string | null>(null, [Validators.required]),
    target_warehouse_id: new FormControl<string | null>(null),
    currency: new FormControl<CURRENCY_TYPE | null>(null),
    reason: new FormControl<string | null>('', [Validators.maxLength(1000)]),
    manager_id: new FormControl<string | null>(null),
  });

  protected document: StockDocument | null = null;
  /** Active warehouses plus the archived ones the document already uses */
  protected warehouses: Warehouse[] = [];
  private allWarehouses: Warehouse[] = [];
  protected managers: ManagerOption[] = [];
  protected rows: LineRow[] = [];
  protected shortages = new Map<string, StockShortage>();
  /** Translation key */
  protected formError: string | null = null;

  protected loading = true;
  protected loadFailed = false;
  protected busy = false;
  protected linesDirty = false;
  protected closePanelOpen = false;
  protected closeResolution: TransferCloseResolution = 'RETURN';
  protected closeReason = '';
  private changed = false;
  private lineColumnsCache: { key: string; columns: TableV2Column<LineRow>[] } | null = null;

  protected readonly lineActions: TableV2Action<LineRow>[] = [
    { icon: 'delete', tooltip: 'delete', tone: 'danger', click: (row) => this.removeRow(row) },
  ];
  protected readonly shortageRowClass = (row: LineRow): string =>
    this.shortages.has(row.variant_id) ? 'bg-rose-50/60' : '';

  protected readonly typeStyles = DOCUMENT_TYPE_STYLES;
  protected readonly statusClasses = DOCUMENT_STATUS_CLASSES;
  protected readonly currencyOptions = CURRENCY_OPTIONS;
  protected readonly formatQuantity = formatQuantity;
  protected readonly formatMoneyAmount = formatMoneyAmount;
  protected readonly formatFullName = formatFullName;
  protected readonly getDocumentNumber = getDocumentNumber;
  protected readonly getVariantTitle = getVariantTitle;
  protected readonly getVariantCodes = getVariantCodes;

  get isDraft(): boolean {
    return this.document?.status !== 'POSTED' && this.document?.status !== 'CANCELLED';
  }

  get isOpenTransfer(): boolean {
    return isOpenTransfer(this.document);
  }

  get hasChanges(): boolean {
    return this.isDraft && (this.form.dirty || this.linesDirty);
  }

  /** Picked, or of the first line product like the backend does */
  get currency(): CURRENCY_TYPE {
    return this.form.controls.currency.value ?? this.rows[0]?.variant?.currency ?? CURRENCY_TYPE.UAH;
  }

  get total(): number {
    if (!this.isDraft) return this.document?.amount ?? 0;
    return this.rows.reduce((sum, row) => sum + (this.getRowAmount(row) ?? 0), 0);
  }

  get totalInTransit(): number {
    return this.rows.reduce((sum, row) => sum + (row.line?.in_transit_quantity ?? 0), 0);
  }

  /** Columns by the type and state of the document; cached, as a new array re-renders the table */
  get lineColumns(): TableV2Column<LineRow>[] {
    const key = `${this.type}|${this.isDraft}|${this.isOpenTransfer}`;
    if (this.lineColumnsCache?.key !== key) this.lineColumnsCache = { key, columns: this.buildLineColumns() };
    return this.lineColumnsCache.columns;
  }

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.loading = true;
    this.loadFailed = false;
    forkJoin({
      warehouses: this.warehouseService.getWarehouses(),
      managers: this.warehouseService.getManagers(),
      document: this.documentId ? this.warehouseService.getDocument(this.documentId) : of(null),
    })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ warehouses, managers, document }) => {
          this.allWarehouses = warehouses;
          this.managers = managers;
          if (document) return this.applyDocument(document);

          const active = warehouses.filter((warehouse) => !warehouse.is_archived);
          this.warehouses = active;
          this.form.reset({
            warehouse_id: active[0]?.id ?? null,
            target_warehouse_id: this.type === 'TRANSFER' ? (active[1]?.id ?? null) : null,
            reason: '',
            manager_id: this.baseService.user?.id ?? null,
          });
        },
        error: (error) => {
          this.loadFailed = true;
          this.warehouseService.error(error);
        },
      });
  }

  // #region lines

  /** A variant picked twice adds one more instead of a second line, handy with a barcode scanner */
  protected addVariant(variant: VariantPick): void {
    const field = this.type === 'INVENTORY' ? 'actual_quantity' : 'quantity';
    let row = this.rows.find((item) => item.variant_id === variant.id);
    if (!row) {
      const currency = this.form.controls.currency;
      if (!this.rows.length && !currency.value) currency.setValue(variant.currency);
      row = this.createRow({ variant_id: variant.id, variant });
      // A receipt starts from the cost price, the usual purchase price of the next delivery
      row.price = this.type === 'RECEIPT' && variant.cost_price ? String(variant.cost_price) : '';
      this.rows = [...this.rows, row];
    }
    row[field] = formatQuantity((parseNumber(row[field], 3) ?? 0) + 1).replace(/\s/g, '');
    this.markLinesDirty();
  }

  protected removeRow(row: LineRow): void {
    this.rows = this.rows.filter((item) => item !== row);
    this.markLinesDirty();
  }

  protected onRowInput(row: LineRow, field: LineField, value: string): void {
    row[field] = value;
    row.invalid.delete(field);
    if (field !== 'accept') this.markLinesDirty();
  }

  protected getRowAmount(row: LineRow): number | null {
    if (row.line && !this.isDraft) return row.line.amount;

    const quantity = parseNumber(row.quantity, 3);
    const price = parseNumber(row.price, 4);
    return quantity === null || price === null ? null : quantity * price;
  }

  protected getManager(userId: string | null): ManagerOption | undefined {
    return this.managers.find((manager) => manager.id === userId);
  }

  private markLinesDirty(): void {
    this.linesDirty = true;
    this.shortages.clear();
    this.formError = null;
  }

  // #endregion

  // #region actions

  protected saveDraft(): void {
    if (this.validate(false)) this.run(this.persistDraft(), 'stock.document.draft_saved');
  }

  protected post(): void {
    if (!this.validate(true)) return;

    const draft$ = this.hasChanges || !this.document ? this.persistDraft() : of(this.document);
    this.run(
      draft$.pipe(switchMap((document) => this.warehouseService.documentAction(document.id, 'post'))),
      'stock.document.posted',
      'stock.document.post_confirm',
    );
  }

  protected cancelDocument(): void {
    if (!this.document) return;

    const cancel$ = this.warehouseService.documentAction(this.document.id, 'cancel');
    this.run(cancel$, 'stock.document.cancelled', 'stock.document.cancel_confirm');
  }

  /** A draft that was never saved is just dropped, after a confirmation when something was entered */
  protected deleteDraft(): void {
    const document = this.document;
    if (!document) return this.requestClose();
    if (this.busy) return;

    this.warehouseService
      .confirm('stock.document.delete_confirm')
      .pipe(
        tap(() => (this.busy = true)),
        switchMap(() =>
          this.warehouseService.remove('document', document.id).pipe(finalize(() => (this.busy = false))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.warehouseService.success('stock.document.deleted');
          this.close.emit(true);
        },
        error: (error) => this.handleError(error),
      });
  }

  /** Accepts the typed quantities; when they cover everything in transit the whole rest is accepted */
  protected acceptTransfer(): void {
    if (!this.document) return;

    this.rows.forEach((row) => {
      const quantity = row.accept.trim() ? parseNumber(row.accept, 3) : 0;
      if (quantity === null || quantity > (row.line?.in_transit_quantity ?? 0)) row.invalid.add('accept');
    });
    if (this.rows.some((row) => row.invalid.has('accept'))) {
      this.formError = 'stock.errors.STOCK_DOCUMENT_INVALID.more_than_in_transit';
      return;
    }

    const lines = this.rows
      .filter((row) => row.line && parseNumber(row.accept, 3))
      .map((row) => ({ line_id: row.line!.id, quantity: parseNumber(row.accept, 3)! }));
    if (!lines.length) {
      this.formError = 'stock.errors.STOCK_DOCUMENT_INVALID.nothing_to_accept';
      return;
    }
    const acceptsAll = this.rows.every(
      (row) => !row.line?.in_transit_quantity || parseNumber(row.accept, 3) === row.line.in_transit_quantity,
    );
    this.run(
      this.warehouseService.documentAction(this.document.id, 'accept', acceptsAll ? {} : { lines }),
      'stock.document.accepted',
    );
  }

  protected closeTransfer(): void {
    const reason = this.closeReason.trim() || null;
    if (!this.document) return;
    if (this.closeResolution === 'LOSS' && !reason) {
      this.formError = 'stock.document.loss_reason_required';
      return;
    }

    const close$ = this.warehouseService.documentAction(this.document.id, 'close', {
      resolution: this.closeResolution,
      reason,
    });
    this.run(close$, 'stock.document.transfer_closed');
  }

  protected openCard(cardId: string): void {
    this.stackedSidebarService
      .openSidebar({ type: SidebarType.CardV3, width: '90vw' }, { card_id: cardId, type: FUNNEL_TYPE.DEAL })
      .subscribe();
  }

  protected openMovements(documentId: string): void {
    this.close.emit(this.changed);
    void this.router.navigate([this.baseService.routerSettings('/warehouse/movements')], {
      queryParams: { document_id: documentId },
    });
  }

  protected openDocument(documentId: string): void {
    this.warehouseService.openDocument(documentId).subscribe();
  }

  /** Called by the sidebar close button: an unsaved draft asks first */
  public requestClose(): void {
    if (this.busy) return;

    this.warehouseService
      .confirm(this.hasChanges ? 'stock.unsaved_confirm' : null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.close.emit(this.changed));
  }

  /** A request of the document with the busy flag, after a confirmation when there is `confirmKey` */
  private run(request$: Observable<StockDocument>, successKey: string, confirmKey?: string): void {
    if (this.busy) return;

    this.warehouseService
      .confirm(confirmKey ?? null)
      .pipe(
        tap(() => (this.busy = true)),
        switchMap(() => request$.pipe(finalize(() => (this.busy = false)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (document) => {
          this.applyDocument(document, true);
          this.warehouseService.success(successKey);
        },
        error: (error) => this.handleError(error),
      });
  }

  /** Creates the draft or saves its changes, the stock does not move; a retry after an error saves the same draft */
  private persistDraft(): Observable<StockDocument> {
    const { target_warehouse_id: targetId, currency, reason, ...raw } = this.form.getRawValue();
    const lines = this.rows.map((row) => ({
      variant_id: row.variant_id,
      ...(this.type === 'INVENTORY'
        ? { actual_quantity: parseNumber(row.actual_quantity, 3) ?? 0 }
        : { quantity: parseNumber(row.quantity, 3, this.type === 'ADJUSTMENT') ?? 0 }),
      ...(this.type === 'RECEIPT' && { price: parseNumber(row.price, 4) ?? 0 }),
    }));

    return this.warehouseService
      .saveDocument(this.document?.id ?? null, {
        ...raw,
        type: this.document ? undefined : this.type,
        target_warehouse_id: this.type === 'TRANSFER' ? targetId : null,
        currency: currency ?? undefined,
        reason: reason?.trim() || null,
        lines,
      })
      .pipe(tap((document) => this.applyDocument(document, true)));
  }

  // #endregion

  // #region state

  private applyDocument(document: StockDocument, changed = false): void {
    this.changed ||= changed;
    this.document = document;
    this.type = document.type;
    this.closePanelOpen = false;
    this.warehouses = this.allWarehouses.filter(
      (item) => !item.is_archived || item.id === document.warehouse_id || item.id === document.target_warehouse_id,
    );
    this.form.reset(document);
    this.rows = document.lines.map((line) =>
      this.createRow({
        ...line,
        quantity: String(line.quantity),
        price: line.price === null ? '' : String(line.price),
        actual_quantity: line.actual_quantity === null ? '' : String(line.actual_quantity),
        accept: line.in_transit_quantity ? String(line.in_transit_quantity) : '',
        line,
      }),
    );
    this.linesDirty = false;
    this.shortages.clear();
    this.formError = null;
  }

  private createRow(row: Partial<LineRow> & Pick<LineRow, 'variant_id' | 'variant'>): LineRow {
    return { quantity: '', price: '', actual_quantity: '', accept: '', line: null, ...row, invalid: new Set() };
  }

  private buildLineColumns(): TableV2Column<LineRow>[] {
    const { type, isDraft } = this;
    type Column = TableV2Column<LineRow>;
    const column = (key: string, header: string, more: Partial<Column> = {}): Column => ({
      key,
      header,
      align: 'right',
      nowrap: true,
      class: 'w-32',
      ...more,
    });
    const quantity = (
      key: string,
      header: string,
      cellClass: string,
      get: (line: StockDocumentLine) => number | null,
    ): Column => column(key, header, { cellClass, value: (row) => row.line && formatQuantity(get(row.line)) });
    const signed = (header: string): Column =>
      column('signed', header, {
        value: (row) => formatSignedQuantity(row.line?.quantity ?? 0),
        cellClass: ({ line }) =>
          `font-semibold ${line?.quantity ? (line.quantity > 0 ? 'text-emerald-600' : 'text-rose-600') : 'text-slate-400'}`,
      });
    const money = (amount: number | null | undefined): string | null =>
      amount === null || amount === undefined ? null : formatMoneyAmount(amount, this.currency);
    const quantityHeader =
      { TRANSFER: 'stock.document.sent', ADJUSTMENT: 'stock.document.change', INVENTORY: 'stock.document.actual' }[
        type as string
      ] ?? 'stock.quantity';

    const columns: Column[] = [{ key: 'product', header: 'stock.product', padding: 'px-4' }];
    if (isDraft) {
      columns.push(column(type === 'INVENTORY' ? 'actual_quantity' : 'quantity', quantityHeader));
      if (type === 'RECEIPT') columns.push(column('price', 'stock.document.purchase_price', { class: 'w-36' }));
    } else if (type === 'INVENTORY') {
      columns.push(
        quantity('expected', 'stock.document.expected', 'text-slate-600', (line) => line.expected_quantity),
        quantity('actual', quantityHeader, 'font-medium text-slate-800', (line) => line.actual_quantity),
        signed('stock.document.difference'),
      );
    } else if (type === 'ADJUSTMENT') {
      columns.push(signed(quantityHeader));
    } else {
      const sent = (row: LineRow): string => `${formatQuantity(row.line?.quantity)} ${row.variant?.unit ?? ''}`;
      columns.push(column('sent', quantityHeader, { value: sent, cellClass: 'font-medium text-slate-800' }));
      if (type === 'TRANSFER') {
        columns.push(
          quantity(
            'accepted',
            'stock.document.accepted_quantity',
            'text-emerald-700',
            (line) => line.accepted_quantity,
          ),
          quantity('in_transit', 'stock.in_transit', 'text-sky-700', (line) => line.in_transit_quantity),
        );
      }
      if (this.isOpenTransfer) columns.push(column('accept', 'stock.document.accept_now'));
    }
    if (!isDraft) {
      const header = type === 'RECEIPT' ? 'stock.document.purchase_price' : 'stock.cost_price';
      columns.push(
        column('cost', header, { class: 'w-36', value: (row) => money(row.line?.price), cellClass: 'text-slate-600' }),
      );
    }
    if (type === 'RECEIPT' || !isDraft) {
      const amount = (row: LineRow): string | null => money(this.getRowAmount(row));
      columns.push(column('amount', 'amount', { value: amount, cellClass: 'font-medium text-slate-800' }));
    }
    return columns;
  }

  /** The checks of the backend; posting needs at least one line */
  private validate(forPosting: boolean): boolean {
    this.form.markAllAsTouched();
    this.rows.forEach((row) => {
      const quantity = parseNumber(row.quantity, 3, this.type === 'ADJUSTMENT');
      if (this.type === 'INVENTORY' && parseNumber(row.actual_quantity, 3) === null) row.invalid.add('actual_quantity');
      if (this.type !== 'INVENTORY' && !quantity) row.invalid.add('quantity');
      if (this.type === 'RECEIPT' && parseNumber(row.price, 4) === null) row.invalid.add('price');
    });

    const { warehouse_id: warehouseId, target_warehouse_id: targetId } = this.form.getRawValue();
    const isTransfer = this.type === 'TRANSFER';
    this.formError = !warehouseId
      ? 'stock.document.warehouse_required'
      : isTransfer && !targetId
        ? 'stock.document.target_required'
        : isTransfer && targetId === warehouseId
          ? 'stock.errors.STOCK_DOCUMENT_INVALID.same_warehouse'
          : this.rows.some((row) => row.invalid.size)
            ? 'stock.document.lines_invalid'
            : forPosting && !this.rows.length
              ? 'stock.errors.STOCK_DOCUMENT_INVALID.empty'
              : null;
    return !this.formError && this.form.valid;
  }

  /** Marks the shortages and the line the backend named, its `field` is a field of the row */
  private handleError(error: unknown): void {
    this.shortages = new Map(getStockShortages(error).map((shortage) => [shortage.variant_id, shortage]));
    const { code, details = {} } = getApiError(error);
    const row = code === 'STOCK_DOCUMENT_INVALID' ? this.rows[details['line'] as number] : undefined;
    row?.invalid.add(details['field'] as LineField);

    this.warehouseService.error(
      error,
      (variantId) => getVariantTitle(this.rows.find((item) => item.variant_id === variantId)?.variant) || null,
    );
  }

  // #endregion
}
